import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { preventFraming } from "@/security/frameProtection";
import { disableDangerousAPIs } from "@/security/permissions";
import SiteLayout from "./components/SiteLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import HomePage from "./pages/Index";

const RatesPage = lazy(() => import("./pages/Rates"));
const IndustriesPage = lazy(() => import("./pages/Industries"));
const MarketsPage = lazy(() => import("./pages/Markets"));
const QuotePage = lazy(() => import("./pages/Quote"));
const UploadPage = lazy(() => import("./pages/Upload"));
const ContactPage = lazy(() => import("./pages/Contact"));
const LoginPage = lazy(() => import("./pages/Login"));
const RegisterPage = lazy(() => import("./pages/Register"));
const DashboardPage = lazy(() => import("./pages/Dashboard"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDocuments = lazy(() => import("./pages/admin/AdminDocuments"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));
const AdminDebug = lazy(() => import("./pages/admin/AdminDebug"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    preventFraming();
    disableDangerousAPIs();
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={null}>
            <Routes>
              <Route element={<SiteLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/rates" element={<RatesPage />} />
                <Route path="/industries" element={<IndustriesPage />} />
                <Route path="/markets" element={<MarketsPage />} />
                <Route path="/quote" element={<QuotePage />} />
                <Route path="/upload" element={<UploadPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                  <Route index element={<Navigate to="/admin/documents" replace />} />
                  <Route path="documents" element={<AdminDocuments />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="contacts" element={<AdminContacts />} />
                  <Route path="debug" element={<AdminDebug />} />
                </Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
